import {
  Baggage,
  CheckInFlight,
  Journey,
  JourneyElement,
  JourneyElementCommon,
  JourneyUnpaidBaggageItem,
  JourneyUnpaidSeatItem,
  SeatmapSeatProperties,
  ServiceItem,
} from '@dapi/sdk';
import {
  getFormattedAmount,
  getFormattedTotal,
  getShortDescriptionContent,
} from '@dapi/sdk/helpers';
import {
  JourneyUnpaidItemsState,
} from '@des/store/journey-unpaid-items';
import {
  Dictionary,
} from '@ngrx/entity';
import {
  AncillaryService,
  buildBoundIdFromJourney,
  buildFlightIdFromCheckinFlights,
  getMethodOfPayment,
  getPaymentType,
  Item,
  Payment,
  Product,
} from '@refx/analytics-core';
import {
  BAGGAGE_CATEGORY,
  generatePersistentId,
  getSsrCode,
} from '@refx/shared-common';
import {
  SelectedServicesModel,
} from '@refx/shared-store';
import {
  CMBAGID,
  getServiceId,
} from '@refx/ssci-common';
import {
  SsciPaymentStatusModel,
  SsciPaymentStatusState,
} from '@refx/ssci-store';

const enum PaymentStatus {
  paid = 'paid',
  selected = 'selected'
}

/**
 * Interface for transaction data
 */
export interface TransactionData {
  payment?: Payment;
  products: Product[];
}

/**
 * Generate seat id from journey element id and seatnumber
 *
 * @param journeyElement
 */
function generateSeatId(journeyElement: JourneyElement & {seat: SeatmapSeatProperties;}): string {
  return journeyElement.id + (journeyElement.seat.seatNumber || '');
}

/**
 * Get all CM bags (unpaid + free) for specific traveler
 * NB: All bags booked through ATPCO during checkin will be counted as services and not bags, so will not be logged from here
 *
 * @param travelerId
 * @param journey
 * @param unpaidItems
 * @param serviceCategoryMap
 * @param selectedBaggageIds
 * @param analyticsProducts
 */
export function getTravelerTransactionBags(
  travelerId?: string,
  journey?: Journey,
  unpaidItems?: JourneyUnpaidItemsState,
  serviceCategoryMap?: Dictionary<string>,
  selectedBaggageIds?: string[],
  analyticsProducts?: Product[]
): Product[] {
  // Before bag checkin step, all bags payed during booking are present in journey.services
  // Once checkin performed, bags should be both in journey.baggage and journey.services.
  // If adding a bag through CM, baggage should appear in journey.baggage till it is paid (then in both places)
  // If adding a bag through ATPCO, baggage should appear in both in journey.baggage and journey.services once paid and back to overview
  let journeyBags: Baggage[] = journey && journey.baggages && journey.baggages.filter(
    (baggage) => !!baggage.travelerIds.find(
      (tid) => tid.id === travelerId
    )
  ) || [];
  const nbNewAddedBags = travelerId && selectedBaggageIds?.filter((cmBagId) => cmBagId === `${CMBAGID}${travelerId}`)?.length || 0;
  const unpaidBag: JourneyUnpaidBaggageItem | undefined = (unpaidItems?.model?.baggage || []).find((bag) => bag.travelerId === travelerId);
  const paidBagsQuantity = serviceCategoryMap && (journey?.services || [])
    .filter((service) => serviceCategoryMap[getSsrCode(service)] === BAGGAGE_CATEGORY
      && service.travelerId === travelerId
      && !(unpaidItems?.model?.services || []).find((bag) => getServiceId(bag) === service.id))
    .reduce((countedBags: number, bagToCount: ServiceItem) => countedBags + (bagToCount.quantity || 1), 0)
    || 0;
  const unpaidBagQuantity: number = unpaidBag && unpaidBag.quantity || 0;
  const indexToUpdate = journeyBags.length - unpaidBagQuantity;
  journeyBags = journeyBags.slice(0, Math.max(journeyBags.length - paidBagsQuantity, 0));
  const fromIndexNewAddedBags = journeyBags.length - nbNewAddedBags;

  return journeyBags.reduce((transactionBags: Product[], journeyBag, index) => {
    if (index <= indexToUpdate) {
      // as there is no id matching, we need to rely on index in journey baggages and number of unpaid bags
      const isAdded = index >= Math.min(fromIndexNewAddedBags, indexToUpdate);
      const isUnpaid = index === indexToUpdate && !!unpaidBag;
      const savedProductPrice = analyticsProducts?.find((p) => p.productInfo?.productID === journeyBag.id)?.productInfo?.productDetails?.ancillaryService?.price;
      transactionBags.push({
        category: {
          primaryCategory: 'AncillaryService'
        },
        productInfo: {
          productID: journeyBag.id,
          productDetails: {
            ancillaryService: {
              categoryCode: 'BAG',
              serviceCode: 'XBAG',
              status: !isAdded && !isUnpaid ? PaymentStatus.paid : PaymentStatus.selected,
              price: {
                totalPrice: isUnpaid
                  ? {
                    amount: getFormattedAmount(unpaidBag.amount),
                    currency: unpaidBag.amount.currencyCode
                  }
                  : {
                    amount: savedProductPrice?.totalPrice.amount || 0,
                    currency: savedProductPrice?.totalPrice.currency || ''
                  }
              },
              quantity: isUnpaid ? unpaidBagQuantity : 1,
              travellerID: travelerId,
              boundID: journey && buildBoundIdFromJourney(journey)
            }
          }
        }
      });
    }
    return transactionBags;
  }, []);
}

/**
 * Get all bags (unpaid + free)
 *
 * @param unpaidItems
 * @param journey
 * @param serviceCategoryMap
 * @param selectedBaggageIds
 * @param analyticsProducts
 */
function getTransactionBags(
  unpaidItems?: JourneyUnpaidItemsState,
  journey?: Journey,
  serviceCategoryMap?: Dictionary<string>,
  selectedBaggageIds?: string[],
  analyticsProducts?: Product[]
): Product[] {
  return journey && journey.travelers.reduce(
    (tbags: Product[], traveler) => tbags.concat(getTravelerTransactionBags(traveler.id, journey, unpaidItems, serviceCategoryMap, selectedBaggageIds, analyticsProducts)), []
  ) || [];
}

/**
 * Get all seats (unpaid + free)
 *
 * @param unpaidSeats
 * @param journey
 * @param selectedSeatIds
 * @param analyticsProducts
 */
export function getTransactionSeats(
  unpaidSeats: JourneyUnpaidSeatItem[],
  journey?: Journey,
  selectedSeatIds?: string[],
  analyticsProducts?: Product[]
): Product[] {
  const journeyElements: (JourneyElementCommon & {journeyElement: JourneyElement & {seat: SeatmapSeatProperties;};})[] = journey ? journey.journeyElements
    .filter((journeyElement): journeyElement is JourneyElementCommon & {journeyElement: JourneyElement & {seat: SeatmapSeatProperties;};} => !!journeyElement.journeyElement.seat)
    : [];

  return journeyElements.map(({journeyElement}) => {
    const linkedUnpaidSeat = unpaidSeats.find((unpaidSeat) =>
      unpaidSeat.travelerId === journeyElement.travelerId && unpaidSeat.seatNumber === journeyElement.seat.seatNumber
    );

    const seatId = generateSeatId(journeyElement);
    const isAdded = !!selectedSeatIds?.includes(seatId);
    const savedProductPrice = analyticsProducts?.find((p) => p.productInfo?.productID === seatId)?.productInfo?.productDetails?.ancillaryService?.price;

    return {
      category: {
        primaryCategory: 'AncillaryService'
      },
      productInfo: {
        productID: seatId,
        productDetails: {
          ancillaryService: {
            segmentID: buildFlightIdFromCheckinFlights([journeyElement.flight]),
            boundID: journey && buildBoundIdFromJourney(journey),
            categoryCode: 'SEAT',
            serviceCode: 'SEAT',
            status: !isAdded && !linkedUnpaidSeat ? PaymentStatus.paid : PaymentStatus.selected,
            price: {
              totalPrice: {
                amount: linkedUnpaidSeat && getFormattedAmount(linkedUnpaidSeat.amount) || savedProductPrice?.totalPrice.amount || 0,
                currency: linkedUnpaidSeat && linkedUnpaidSeat.amount.currencyCode || savedProductPrice?.totalPrice.currency || ''
              }
            },
            quantity: 1,
            travellerID: journeyElement.travelerId
          }
        }
      }
    };
  });
}

/**
 * Get all other services
 *
 * @param unpaidServices
 * @param journey
 * @param englishCategoriesNames
 * @param englishServicesNames
 * @param serviceCategoryMap
 * @param selectedServiceIds
 * @param analyticsProducts
 */
export function getTransactionServices(
  unpaidServices: ServiceItem[],
  journey?: Journey,
  englishCategoriesNames?: Dictionary<string>,
  englishServicesNames?: Dictionary<string>,
  serviceCategoryMap?: Dictionary<string>,
  selectedServiceIds?: string[],
  analyticsProducts?: Product[]
): Product[] {
  const services = getAllServices(unpaidServices, journey?.services);
  return services.reduce((products: Product[], service: ServiceItem) => {
    const serviceCode = getShortDescriptionContent(service) || '';
    const categoryCode = serviceCategoryMap?.[serviceCode] || '';
    const productID = [getSsrCode(service), service.flightIds, service.travelerId].join('-');
    const productFound = products.find((p) => p.productInfo?.productID === productID);
    const isAdded = !!selectedServiceIds?.find((serviceId: string) => generatePersistentId(service) === serviceId);
    const isPaid = isServicePaid(service, unpaidServices);
    const flights = getFlightsFromFlightIds(journey, service.flightIds);
    const savedProductPrice = analyticsProducts?.find((p) => p.productInfo?.productID === productID)?.productInfo?.productDetails?.ancillaryService?.price;
    if (productFound?.productInfo?.productDetails?.ancillaryService?.quantity) {
      productFound.productInfo.productDetails.ancillaryService.quantity += (service.quantity || 1);
    } else if (flights.length > 0) {
      products.push({
        category: {
          primaryCategory: 'AncillaryService'
        },
        productInfo: {
          productID,
          productDetails: {
            ancillaryService: {
              boundID: journey && buildBoundIdFromJourney(journey),
              categoryCode,
              categoryName: englishCategoriesNames?.[categoryCode] || '',
              serviceCode,
              serviceName: englishServicesNames?.[serviceCode] || '',
              ...(service.flightIds?.length === 1 && {
                segmentID: buildFlightIdFromCheckinFlights(flights)
              }),
              price: {
                totalPrice: {
                  amount:
                    (service.prices?.totalPrices && service.prices.totalPrices[0] && service.prices.totalPrices[0].total && getFormattedTotal(service.prices.totalPrices[0]))
                    || savedProductPrice?.totalPrice.amount || 0,
                  currency: (service.prices?.totalPrices && service.prices.totalPrices[0] && service.prices.totalPrices[0].currencyCode)
                    || savedProductPrice?.totalPrice.currency || ''
                }
              },
              status: isAdded && isPaid ? PaymentStatus.paid : PaymentStatus.selected,
              quantity: service.quantity || 1,
              travellerID: service.travelerId
            }
          }
        }
      });
    }
    return products;
  }, []);
}

/**
 * get payment
 *
 * @param checkInPaymentRecordId
 * @param items
 * @param paymentStatus
 * @param unpaidItems
 */
function getTransactionPayment(
  checkInPaymentRecordId: string,
  items: Item[],
  paymentStatus?: SsciPaymentStatusState,
  unpaidItems?: JourneyUnpaidItemsState
): Payment | undefined {
  const paymentTransaction: SsciPaymentStatusModel | undefined = paymentStatus && paymentStatus.model;
  const paymentType = paymentTransaction && paymentTransaction.paymentMethod && getPaymentType({
    paymentMethod: paymentTransaction.paymentMethod
  });
  const methodOfPayment = paymentTransaction && paymentTransaction.paymentMethod && getMethodOfPayment({
    paymentMethod: paymentTransaction.paymentMethod
  });
  return methodOfPayment && paymentType && unpaidItems && unpaidItems.model ? {
    methodOfPayment,
    paymentReference: checkInPaymentRecordId,
    paymentType,
    item: items,
    price: {
      totalPrice: {
        amount: getFormattedAmount(unpaidItems.model.total),
        currency: unpaidItems.model.total.currencyCode
      }
    }
  } : undefined;
}

/**
 * Get item from product
 *
 * @param product
 */
function getItemFromProduct(product: Product): Item | undefined {
  const ancillaryService: AncillaryService | undefined = product.productInfo && product.productInfo.productDetails && product.productInfo.productDetails.ancillaryService;
  if (ancillaryService && ancillaryService.price && ancillaryService.price.totalPrice && ancillaryService.price.totalPrice.amount) {
    return {
      productInfo: {
        productID: product.productInfo!.productID || '',
        quantity: ancillaryService.quantity || 1
      }
    };
  }
}
/**
 * Get transaction data
 *
 * @param journey
 * @param paymentStatus
 * @param unpaidItems
 * @param checkInPaymentRecordId
 * @param englishCategoriesNames
 * @param englishServicesNames
 * @param serviceCategoryMap
 * @param selectedServices
 * @param analyticsProducts
 */
export function getTransactionData(
  journey: Journey | undefined,
  paymentStatus?: SsciPaymentStatusState,
  unpaidItems?: JourneyUnpaidItemsState,
  checkInPaymentRecordId = '',
  englishCategoriesNames?: Dictionary<string>,
  englishServicesNames?: Dictionary<string>,
  serviceCategoryMap?: Dictionary<string>,
  selectedServices?: SelectedServicesModel,
  analyticsProducts?: Product[]
): TransactionData {
  const items: Item[] = [];
  const products: Product[] = [];

  [
    ...getTransactionBags(unpaidItems, journey, serviceCategoryMap, selectedServices?.orderServiceIds?.filter((serviceId) => serviceId.startsWith(CMBAGID)), analyticsProducts),
    ...getTransactionSeats(unpaidItems && unpaidItems.model && unpaidItems.model.seats || [], journey, selectedServices?.orderSeatIds, analyticsProducts),
    ...getTransactionServices(unpaidItems && unpaidItems.model && unpaidItems.model.services || [], journey, englishCategoriesNames, englishServicesNames, serviceCategoryMap,
      selectedServices?.orderServiceIds?.filter((serviceId) => !serviceId.startsWith(CMBAGID)), analyticsProducts)

  ].forEach((service) => {
    const item = getItemFromProduct(service);
    if (item) {
      items.push(item);
    }
    products.push(service);
  });

  const payment: Payment | undefined = getTransactionPayment(checkInPaymentRecordId, items, paymentStatus, unpaidItems);

  return {
    payment,
    products
  };
}

/**
 * Get transaction PNR
 *
 * @param journey
 */
export function getTransactionPnr(journey: Journey | undefined) {
  return {
    recLoc: (journey && journey.journeyElements && journey.journeyElements[0] && journey.journeyElements[0].journeyElement.orderId) || '',
    modificationDate: new Date().toISOString()
  };
}

/**
 * Is true is service is paid, false otherwise
 *
 * @param service
 * @param unpaidServices
 * @returns TRUE if service is paid, FALSE otherwise
 */
function isServicePaid(service: ServiceItem, unpaidServices: ServiceItem[]): boolean {
  return !unpaidServices.find((unpaidService) => getServiceId(unpaidService) === getServiceId(service));
}

/**
 * Returns unpaid/journey services aggregation without baggage ones
 *
 * @param unpaidServices
 * @param journeyServices
 * @returns unpaid/journey services aggregation
 */
function getAllServices(unpaidServices: ServiceItem[], journeyServices: ServiceItem[] = []) {
  return unpaidServices.concat(
    journeyServices.filter((service) => !unpaidServices.find((unpaidService) => getServiceId(unpaidService) === service.id))
  );
}

/**
 * Get the list of flights related to flight ids
 *
 * @param journey journey
 * @param flightIds ids of flight
 * @returns the list of flight
 */
function getFlightsFromFlightIds(journey?: Journey, flightIds: string[] = []): CheckInFlight[] {
  if (!journey) {
    return [];
  }
  return flightIds
    .map((flightId) => journey.flights.find((checkinFlightIdObject) => checkinFlightIdObject.checkInFlight.id === flightId)?.checkInFlight)
    .filter((checkinFlight): checkinFlight is CheckInFlight => !!checkinFlight);
}
